import {
  createRootRouteWithContext,
  Outlet,
  useRouter,
  useRouterState,
} from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import NotFound from '@/pages/NotFound'
import type { QueryClient } from '@tanstack/react-query'
import type { AuthContextType } from '@/context/auth-context'
import { initiateAuthentication } from '@/lib/auth-client'
import {
  ensureAuthenticatedUser,
  getAuthRouteFlags,
  throwAccessStateRedirect,
} from '@/features/auth/utils/access-state-navigation'
import { useAuth } from '@/context/useAuth'
import RouterProgressBar from '@/components/common/RouterProgressBar'

interface MyRouterContext {
  queryClient: QueryClient
  auth: AuthContextType
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  beforeLoad: async ({ context, location }) => {
    const routeFlags = getAuthRouteFlags(location.pathname)
    const user = await ensureAuthenticatedUser(context.queryClient)

    if (user) {
      throwAccessStateRedirect(user, routeFlags)
      return
    }

    const searchParams = new URLSearchParams(window.location.search)
    const hasInvitationToken =
      !!searchParams.get('token') || !!searchParams.get('invitationToken')
    if (routeFlags.isInviteRoute && hasInvitationToken) {
      return
    }

    const redirectUrl = routeFlags.isInviteRoute
      ? window.location.href
      : (import.meta.env.VITE_REDIRECT_URL as string)
    const forceLogin =
      new URLSearchParams(window.location.search).get('desktopLogin') === 'true'
    const loginUrl = await initiateAuthentication(redirectUrl, forceLogin)
    if (loginUrl?.redirectUrl) {
      window.location.href = loginUrl.redirectUrl
    }
  },
  component: RootLayout,
  notFoundComponent: NotFound,
})

function RootLayout() {
  const mainRef = useRef<HTMLElement>(null)
  const { location } = useRouterState()
  const { loading: isLoading } = useAuth()
  const { invalidate } = useRouter()

  const prevLoadingRef = useRef(isLoading)
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      invalidate()
    }
    prevLoadingRef.current = isLoading
  }, [invalidate, isLoading])

  useEffect(() => {
    const scrollToTop = () => {
      mainRef.current?.scrollTo({ top: 0, behavior: 'instant' })
      window.scrollTo({ top: 0, behavior: 'instant' })
    }
    const id = setTimeout(scrollToTop, 0)
    return () => clearTimeout(id)
  }, [location.pathname])

  return (
    <>
      <RouterProgressBar />
      <main
        ref={mainRef}
        className="flex-1 overflow-y-auto scrollbar-hide bg-(--color-surface)"
      >
        <Outlet />
      </main>
    </>
  )
}
